export enum MetricEventName {
    // File upload
    FILE_SELECTED = 'FILE_SELECTED',
    INVALID_FILE_SELECTED = 'INVALID_FILE_SELECTED',
    FILE_UPLOAD_START = 'FILE_UPLOAD_START',
    FILE_UPLOAD_COMPLETE = 'FILE_UPLOAD_COMPLETE',
    FILE_UPLOAD_ERROR = 'FILE_UPLOAD_ERROR',
    // Validation & redirect
    VALIDATION_BANNER_SHOWN = 'VALIDATION_BANNER_SHOWN',
    VALIDATION_BANNER_DISMISSED = 'VALIDATION_BANNER_DISMISSED',
    SUGGESTION_LINK_CLICKED = 'SUGGESTION_LINK_CLICKED',
    SUGGESTION_LOADED = 'SUGGESTION_LOADED',
    // File hand-off: the user's file travelling to a suggested tool (T1.2/T1.7).
    // STARTED fires on the origin when the tray is stashed, COMPLETED on the destination
    // when the file is actually staged — the gap between them is where users were lost.
    FILE_HANDOFF_STARTED = 'FILE_HANDOFF_STARTED',
    FILE_HANDOFF_COMPLETED = 'FILE_HANDOFF_COMPLETED',
    FILE_HANDOFF_MISSED = 'FILE_HANDOFF_MISSED',
    /**
     * A file-card preview thumbnail could not be fetched, after an automatic retry.
     *
     * Added because there was no signal at all: a customer reported "the images fail to load"
     * on /convert-png-to-webp and there was nothing in frontend-events to look at. Carries the
     * edge host, because thumbnails come from whichever edge served the upload and a network
     * that blocks those subdomains is the likeliest cause we cannot see from here.
     */
    THUMBNAIL_LOAD_FAILED = 'THUMBNAIL_LOAD_FAILED',
    /** A continuation offered on the result screen was taken (T3.4). */
    NEXT_TOOL_CLICKED = 'NEXT_TOOL_CLICKED',
    /**
     * The outcome of that continuation: did the file actually travel with them (T3.4 phase 2)?
     * CLICKED minus CHAINED-with-carried is the population still re-picking their own file, which
     * is the number that says whether this worked.
     */
    NEXT_TOOL_CHAINED = 'NEXT_TOOL_CHAINED',
    /**
     * Re-edit taken from the tool page rather than from /downloads (T3.3). Measuring this
     * against Re-edit on /downloads is how we learn whether the detour was costing re-runs.
     */
    RECENT_REEDIT_CLICKED = 'RECENT_REEDIT_CLICKED',
    /**
     * A file was re-used from the session list instead of being picked off the device again
     * (T6.2). This is the number that decides whether the tray is worth promoting to a
     * server-backed library (T6.5) or leaving as a browser-local convenience.
     */
    SESSION_FILE_PICKED = 'SESSION_FILE_PICKED',
    /** A session file this page could not take was carried to one that could (T6.4). */
    SESSION_FILE_ROUTED = 'SESSION_FILE_ROUTED',
    // Conversion
    CONVERT_START = 'CONVERT_START',
    CONVERT_COMPLETE = 'CONVERT_COMPLETE',
    CONVERT_ERROR = 'CONVERT_ERROR',
    /**
     * A result file was downloaded from a card. {workflowId, page, source, zip}
     * — `workflowId` (FRAME_PICKER_DESIGN.md F9 / B3) attributes the download
     * to its schedule workflow; `source` is `studio | picker | convert`; `zip`
     * says whether the card handed over a bundle rather than a single file.
     */
    FILE_DOWNLOAD = 'FILE_DOWNLOAD',
    // Auth & errors
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    LOGIN_ERROR = 'LOGIN_ERROR',
    API_ERROR = 'API_ERROR',
    WEBSOCKET_DISCONNECT = 'WEBSOCKET_DISCONNECT',
    // Navigation
    PAGE_VIEW = 'PAGE_VIEW',
    FEATURE_SEARCH = 'FEATURE_SEARCH',
    FEATURE_SEARCH_CLICK = 'FEATURE_SEARCH_CLICK',
    // Funnel spine (improvement-sweep P2 logs-to-add): the modal preview was
    // server-flag-only, downloads had no session context, and option usage and
    // upload abandonment were guesses.
    /** Preview modal opened from a result card. {workflowId, kind} */
    PREVIEW_OPENED = 'PREVIEW_OPENED',
    // Image editor v2 (docs/proposed/IMAGE_EDITOR_V2.md) — 2026-09-06
    EDITOR_FILES_STAGED = 'EDITOR_FILES_STAGED',
    EDITOR_PICKER_OPENED = 'EDITOR_PICKER_OPENED',
    EDITOR_FILE_OPENED = 'EDITOR_FILE_OPENED',
    EDITOR_FILE_FAILED = 'EDITOR_FILE_FAILED',
    EDITOR_TOOL = 'EDITOR_TOOL',
    EDITOR_EXPORT_START = 'EDITOR_EXPORT_START',
    EDITOR_EXPORT_DONE = 'EDITOR_EXPORT_DONE',
    EDITOR_EXPORT_FAILED = 'EDITOR_EXPORT_FAILED',
    /**
     * Page hidden while files were staged but never converted — the measured
     * form of the ~23% upload→convert loss. {page, fileCount}
     */
    UPLOAD_ABANDONED = 'UPLOAD_ABANDONED',
    /** A convert-option knob changed (sampled 1:10 client-side). {key, page} */
    OPTION_CHANGED = 'OPTION_CHANGED',
    // Help-us-grow initiative (2026-08): the link-encouragement surfaces on
    // /downloads and /link-us. Success = credit copies and link-us copies,
    // measured against pitch impressions.
    /** The grow pitch box rendered above the file list. {page} */
    // Downloads-page observability (2026-08-31, free dl% regression investigation):
    // where do users who DON'T download actually go? PAGE_VIEW carries layout facts
    // (adAboveCard), BTN_VISIBLE measures push-down, PAGE_CLICK attributes every
    // control interaction, PAGE_EXIT summarizes the visit on pagehide.
    DOWNLOADS_PAGE_VIEW = 'DOWNLOADS_PAGE_VIEW',
    DOWNLOAD_BTN_VISIBLE = 'DOWNLOAD_BTN_VISIBLE',
    DOWNLOADS_PAGE_CLICK = 'DOWNLOADS_PAGE_CLICK',
    DOWNLOADS_PAGE_EXIT = 'DOWNLOADS_PAGE_EXIT',
    GROW_PITCH_SHOWN = 'GROW_PITCH_SHOWN',
    /** The grow pitch box was dismissed (hidden for 7 days). */
    GROW_PITCH_DISMISSED = 'GROW_PITCH_DISMISSED',
    /** The pitch box CTA to /link-us was clicked. */
    GROW_PITCH_CTA_CLICKED = 'GROW_PITCH_CTA_CLICKED',
    /** Any share-row button on a result card. {channel, tool} */
    SHARE_CLICKED = 'SHARE_CLICKED',

    /**
     * Any same-origin link click that is not already covered by a named CTA event.
     * Attributes are bounded to closed sets by `utils/link-metrics.ts` — never the
     * href, which would blow up Mongo cardinality across ~5,900 pages. The source
     * page needs no attribute: `emit` stamps `pageUrl` on every event.
     */
    INTERNAL_LINK_CLICK = 'INTERNAL_LINK_CLICK',
    /** The "Add us to your site" credit panel was opened. {tool} */
    CREDIT_PANEL_OPENED = 'CREDIT_PANEL_OPENED',
    /** A credit snippet was copied. {format, tool} */
    CREDIT_COPIED = 'CREDIT_COPIED',
    /** A snippet was copied on /link-us. {kind, tool} */
    LINKUS_SNIPPET_COPIED = 'LINKUS_SNIPPET_COPIED',
    // Progress-poller resilience (2026-09-02). On 2026-09-01 one pod host wedged
    // (nginx up, Tomcat dead) and every other replay-history poll hung for the
    // nginx upstream timeout; cards never reached "Completed" and the unique-wsId
    // download rate fell ~87% -> ~62% for four hours. We had no client-side
    // latency signal at all, so these two events are what the timeout will be
    // retuned from.
    /**
     * One progress poll's round trip. {ms, ok, status, endpoint: 'pod'|'edge'}.
     * Sampled 1-in-20 on success (POD_POLL_SAMPLE_EVERY); every failure/timeout
     * emits one.
     */
    POD_POLL_RTT = 'POD_POLL_RTT',
    /**
     * A tracker changed which host it polls for history.
     * {consecutiveFailures, endpoint: 'pod->edge'|'edge->pod'}
     */
    POD_POLL_FALLBACK = 'POD_POLL_FALLBACK',
    /**
     * One Core Web Vital for this page load: {metric, value, rating, page,
     * navType}. Sampled at 10% per page load (see utils/web-vitals.ts) because
     * four events on every load would roughly double the frontend-events
     * volume for a signal that only needs a distribution.
     *
     * Field data, not lab data: the existing core-web-vitals-validator.js runs
     * synthetic Lighthouse passes, which cannot see what real devices on real
     * networks experience — and after the May 2026 core update that difference
     * is the one that ranks.
     */
    WEB_VITALS = 'WEB_VITALS',
    // Hands-off scheduling (clientQueuedState, 2026-09-05 Mongo write-stall
    // incident). One event per card-state transition on the schedule path, so
    // "how often is the server busy, how often does a retry save the card, and
    // how often do we give up" is answerable without reading a single log line.
    //
    // Wire names are dotted (`schedule.queued`, not `SCHEDULE_QUEUED`) on
    // purpose: MONGO-STALL-PLAN-v5.2.md §7 metric 5 and the M6 dashboards key on
    // that namespace, and professorx keeps the raw name in `frontend-events`
    // regardless of its Prometheus label allow-list. professorx
    // `metrics/FrontendEventNames.kt` must gain these five names in M6, until
    // when they aggregate under [OTHER] in `xc_frontend_event` while every
    // document is still stored and queryable. Deliberate; decided 2026-09-05.
    /** A card entered the queued state (503 / 409 / transport failure). {reason, status, attempt} */
    SCHEDULE_QUEUED = 'schedule.queued',
    /** An automatic retry was scheduled. {attempt, delayMs, fromRetryAfter, reason, status} */
    SCHEDULE_RETRY = 'schedule.retry',
    /** A retry came back 200 with the workflowId the first attempt had already created. {attempt} */
    SCHEDULE_DEDUP_HIT = 'schedule.dedup_hit',
    /** The card gave up waiting: budget spent, or the expected-start deadline passed. {attempt, reason} */
    SCHEDULE_STALLED = 'schedule.stalled',
    /** The user pressed the one batch-level "Retry all". {cards, attempt} */
    SCHEDULE_RETRY_ALL = 'schedule.retry_all',

    // Studio — the image→video timeline editor (docs/proposed/STUDIO_DESIGN.md §8).
    //
    // professorx allow-listed all sixteen first, deliberately: `FrontendEventNames.ALLOWED`
    // is a CARDINALITY control, an unknown name is counted as OTHER rather than dropped, and
    // the raw name always reaches the `frontend-events` collection. So leading there costs
    // nothing and lagging there would have left the Studio's first week aggregated under
    // OTHER (`professorx metrics/FrontendEventNames.kt:103-114`). This is the mirror that
    // file's KDoc asks for, and `studio-events.spec.ts` asserts the two lists agree.
    //
    // Attributes are typed in `components/studio/state/studio-events.ts` and never reach a
    // Prometheus label; only the NAME is tagged.

    /** The Studio tab was opened. Not emitted on a restore — that is STUDIO_RESTORED. */
    STUDIO_OPENED = 'STUDIO_OPENED',
    /** The first edit of a session, and how long the user looked before making it. */
    STUDIO_FIRST_EDIT = 'STUDIO_FIRST_EDIT',
    /** Export pressed, with the whole shape of the project — the analysis event. */
    STUDIO_EXPORT_START = 'STUDIO_EXPORT_START',
    STUDIO_EXPORT_COMPLETE = 'STUDIO_EXPORT_COMPLETE',
    /** A refusal or a render failure, carrying the closed `CompositionViolationCode`. */
    STUDIO_EXPORT_FAILED = 'STUDIO_EXPORT_FAILED',
    /** Export could not be attempted — the client's own check said no. */
    STUDIO_EXPORT_BLOCKED = 'STUDIO_EXPORT_BLOCKED',
    /** One per feature per session, so the mix is a count of users not of clicks. */
    STUDIO_FEATURE_USED = 'STUDIO_FEATURE_USED',
    /** On `pagehide`: edits, undos, duration, and whether anything was exported. */
    STUDIO_SESSION_END = 'STUDIO_SESSION_END',
    /** The editor was left with clips on the timeline and nothing exported. */
    STUDIO_ABANDON = 'STUDIO_ABANDON',
    /** A project came back from localStorage or from the URL, and how that went. */
    STUDIO_RESTORED = 'STUDIO_RESTORED',
    /** How long a rendition kept the editor waiting, bucketed. */
    STUDIO_PROXY_WAIT = 'STUDIO_PROXY_WAIT',
    /**
     * The capability probe's result, once per session — the only way to know what the real
     * fleet can do before anything leans on it (§6.7).
     */
    STUDIO_CAPS = 'STUDIO_CAPS',
    /** The 2+-static-images nudge: one name per outcome, so acceptance is a ratio. */
    NUDGE_SHOWN = 'NUDGE_SHOWN',
    NUDGE_ACCEPTED = 'NUDGE_ACCEPTED',
    NUDGE_DECLINED = 'NUDGE_DECLINED',
    NUDGE_DISMISSED = 'NUDGE_DISMISSED',

    // Frame picker — the video→image frame/sequence/sheet tool (FRAME_PICKER_DESIGN.md §8
    // "Frontend"). Names are §8 verbatim; attributes are typed in
    // `components/frame-picker/events.ts` and bucketed there (never raw `t` or sizes).
    // The picker's nudge re-uses `NUDGE_SHOWN` / `NUDGE_DISMISSED` above with `{hasThumbs}`.

    /** The picker was opened. {source: tab|nudge|nudge_thumb|time_link|downloads_link|url} */
    FRAMEPICKER_OPENED = 'FRAMEPICKER_OPENED',
    /** The capability probe's result, once per session. {rvfc, offscreen, imagebitmap, decode} */
    FRAMEPICKER_CAPS = 'FRAMEPICKER_CAPS',
    /** Which source the §6.3 ladder landed on. {kind: original|preview|none, msToFirstFrame} */
    FRAMEPICKER_SOURCE = 'FRAMEPICKER_SOURCE',
    /** A scrub, sampled 10 % client-side. {method: strip|keys|typed|drag|sharpest} */
    FRAMEPICKER_SCRUB = 'FRAMEPICKER_SCRUB',
    /** The big-job check step. {action: shown|back|convert, framesBucket} */
    FRAMEPICKER_CHECK = 'FRAMEPICKER_CHECK',
    /** Convert pressed, with the whole shape of the request — the analysis event. */
    FRAMEPICKER_CONVERT = 'FRAMEPICKER_CONVERT',
    /** The picker was left without converting. {tBucket, mode, hadSource} */
    FRAMEPICKER_ABANDON = 'FRAMEPICKER_ABANDON',
}
